import { Stack } from '@mui/joy';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType, LabelPosition } from 'core/enums';
import { useFormContext } from 'core/states';
import { useGetSalesHistory } from 'parts/hooks';
import { PricingHelper } from 'pricing/helpers';
import { Direction, Size, Variant } from 'theme/enums';

import { usePriceFormFieldsContext } from './state';

interface IProps {
  disabled: boolean;
}

export const UseLatestSalesPrice = ({ disabled }: IProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();

  const { onChangePrice, quote, alternateVendorPartNumber } = usePriceFormFieldsContext();

  const { records } = useGetSalesHistory(
    {
      filters: {
        partNumber: alternateVendorPartNumber,
        salesLocation: quote.location?.externalLocationId,
        billToAccount: quote.billToAddress?.accountsReceivableId,
      },
      page: 1,
      pageSize: 1,
    },
    { enabled: !disabled && !!alternateVendorPartNumber }
  );

  const latestPrice = useMemo(() => {
    if (!records?.length) {
      return null;
    }

    return records[0].sellPrice;
  }, [records]);

  const priceValue = watch('price');

  useEffect(() => {
    if (isChecked && Number(latestPrice) !== Number(priceValue)) {
      setIsChecked(false);
    }
  }, [isChecked, latestPrice, priceValue]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsChecked(event.target.checked);

      if (event.target.checked && latestPrice != null) {
        setValue('price', latestPrice, { shouldValidate: true });
        onChangePrice({ source: 'event', target: { value: latestPrice } });
      }
    },
    [latestPrice, setValue, onChangePrice]
  );

  if (latestPrice == null || disabled) {
    return null;
  }

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
      <FormField
        name="useLatestSalesPrice"
        type={InputType.SWITCH}
        inputProps={{
          checked: isChecked,
          label: t('labelUseLatestPrice'),
          labelPosition: LabelPosition.END,
          size: Size.MD,
          variant: isChecked ? Variant.SOFT : Variant.SOLID,
          onChange: onChange,
        }}
      />

      <FormField
        name="latestSalesPrice"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          value: PricingHelper.formatPricing({ amount: latestPrice }),
          disabled: true,
          sx: { width: 'auto', maxWidth: 120 },
          slotProps: {
            input: {
              prefix: '$',
            },
          },
        }}
      />
    </Stack>
  );
};
